'use client';
import {
  faCircleMinus,
  faCirclePlus,
  faHeart as faHeartRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faChevronLeft,
  faChevronRight,
  faHeart,
  faRotateBack,
  faShoppingCart,
} from '@fortawesome/pro-solid-svg-icons';
import { sendGTMEvent } from '@next/third-parties/google';
import { Dispatch, FC, SetStateAction, useEffect, useMemo } from 'react';
import FeatureFlag from '@/components/common/feature-flag/feature-flag';
import YouTubePlayer from '@/components/common/youtube-player/youtube-player';
import {
  Button,
  Icon,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
} from '@/components/ui';
import useCoreConfig from '@/hooks/use-core-config';
import useFavorites from '@/hooks/use-favorites/use-favorites';
import useMyCart from '@/hooks/use-my-cart/use-my-cart';
import { useTranslation } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import {
  useStudioPreviewPortalRoot,
  useStudioPreviewScrollLock,
} from '@/providers/studio-preview-portal-provider';
import { SimpleProductInfo } from '@/types/products/simple-product-info';
import { cn } from '@/utils';
import { extractYouTubeVideoId, getYouTubeThumbnailUrl } from '@/utils/extract-youtube-video-id';
import { buildGumletUrl } from '@/utils/gumlet';
import { studioPreviewPortalDialogClassNames } from '@/utils/studio-preview-portal-modal';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  selectedIndex: number;
  setSelectedIndex: Dispatch<SetStateAction<number>>;
  zoomLevel: number;
  setZoomLevel: Dispatch<SetStateAction<number>>;
  product: SimpleProductInfo;
}

const ImagesModal: FC<Props> = ({
  isOpen,
  closeModal,
  selectedIndex,
  setSelectedIndex,
  zoomLevel,
  setZoomLevel,
  product,
}) => {
  const { t } = useTranslation();
  const portalRoot = useStudioPreviewPortalRoot();
  const constrainModalToStudioPortal = portalRoot != null;
  useStudioPreviewScrollLock(isOpen && constrainModalToStudioPortal);

  const { keycloak } = useKeycloak();
  const isAuthenticated = keycloak?.authenticated;
  const { features, currency } = useCoreConfig();
  const shouldAllowCart = (!isAuthenticated && features.cartForGuests) || isAuthenticated;
  const { addProductToMyCart, isPendingUpdateMyCartProductId } = useMyCart();
  const { addProductToFavorites, removeProductFromFavorites, favorites, isFavoritePending } =
    useFavorites();

  const isInFavorites = !!favorites.products.find(p => p.id === product.id);

  const images = useMemo(() => {
    const filtered = (product.images ?? [])
      .filter((img): img is string => typeof img === 'string' && img.trim() !== '')
      .map(img => buildGumletUrl(img));
    return filtered.length > 0 ? filtered : ['/imgs/image-not-found.png'];
  }, [product.images]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIndex(prev => Math.min(prev, Math.max(0, images.length - 1)));
  }, [isOpen, images.length, setSelectedIndex]);

  const nextImage = () => {
    if (images.length <= 1) return;
    setSelectedIndex(prev => (prev + 1) % images.length);
    setZoomLevel(1);
  };

  const prevImage = () => {
    if (images.length <= 1) return;
    setSelectedIndex(prev => (prev - 1 + images.length) % images.length);
    setZoomLevel(1);
  };

  const maxZoom = 4;
  const minZoom = 1;

  const handleZoom = (event: React.MouseEvent<HTMLImageElement | HTMLInputElement>) => {
    const img = event.currentTarget;
    const rect = img.getBoundingClientRect();

    const offsetX = ((event.clientX - rect.left) / rect.width) * 100;
    const offsetY = ((event.clientY - rect.top) / rect.height) * 100;

    setZoomLevel(prev => (prev >= maxZoom ? minZoom : prev * 2));

    img.style.transformOrigin = `${offsetX}% ${offsetY}%`;
    img.style.transform = `scale(${zoomLevel})`;
  };

  const safeIndex = Math.min(selectedIndex, Math.max(0, images.length - 1));
  const currentImage = images[safeIndex];
  const currentVideoId = extractYouTubeVideoId(currentImage);

  const getThumbnailUrl = (url: string) => {
    const videoId = extractYouTubeVideoId(url);
    return videoId ? getYouTubeThumbnailUrl(videoId) : url;
  };

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
    setZoomLevel(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      className="flex items-center justify-center"
      size="5xl"
      scrollBehavior="inside"
      portalContainer={portalRoot ?? undefined}
      classNames={constrainModalToStudioPortal ? studioPreviewPortalDialogClassNames : undefined}
    >
      <ModalContent
        className={cn(
          'bg-background z-10 flex flex-col overflow-hidden',
          constrainModalToStudioPortal ? 'max-h-full' : 'max-h-[90dvh] lg:h-[75dvh]',
        )}
      >
        <ModalBody className="bg-background min-h-0 w-full flex-1 overflow-hidden">
          <div
            className={cn(
              'flex h-full min-h-0 flex-col gap-3 lg:flex-row lg:gap-4',
              !constrainModalToStudioPortal && 'max-h-[85dvh] lg:max-h-[60dvh]',
            )}
          >
            <div
              className={cn(
                'bg-background z-10 flex w-full shrink-0 gap-2 overflow-x-auto overflow-y-hidden border-b border-gray-200 p-3 lg:w-1/5 lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:border-r lg:border-b-0 lg:border-r-gray-200 lg:p-4 lg:pt-6',
                constrainModalToStudioPortal
                  ? 'max-h-[28%] lg:h-full lg:max-h-full'
                  : 'max-h-[28dvh] lg:h-full lg:max-h-[60dvh]',
              )}
            >
              <div className="flex gap-2 lg:grid lg:w-full lg:grid-cols-2 lg:gap-2">
                {images.map((img, index) => {
                  const videoId = extractYouTubeVideoId(img);
                  const thumbnailUrl = getThumbnailUrl(img);
                  return (
                    <button
                      type="button"
                      onClick={() => handleThumbnailClick(index)}
                      className="relative h-20 w-20 min-w-20 shrink-0 lg:w-auto lg:min-w-0"
                      key={img}
                    >
                      <Image
                        key={img}
                        src={thumbnailUrl}
                        alt={`Thumbnail ${index}`}
                        width={80}
                        height={80}
                        className={`h-full w-full cursor-pointer border-2 object-cover ${
                          safeIndex === index ? 'border-primary' : 'border-transparent'
                        }`}
                      />
                      {videoId && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <div className="bg-opacity-80 flex h-6 w-6 items-center justify-center rounded bg-red-600">
                            <span className="text-xs text-white">▶</span>
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className={cn(
                'relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden p-2 lg:w-4/5',
                constrainModalToStudioPortal ? 'min-h-[42%]' : 'min-h-[42dvh] lg:min-h-0',
              )}
            >
              <IconButton
                aria-label="Previous image"
                onClick={prevImage}
                isDisabled={images.length <= 1}
                className="bg-background absolute left-2 z-10 text-gray-500 hover:text-black"
              >
                <Icon icon={faChevronLeft} />
              </IconButton>

              <div
                className={`relative flex h-full w-full items-center justify-center overflow-hidden ${
                  currentVideoId ? '' : zoomLevel === 4 ? 'cursor-zoom-out' : 'cursor-zoom-in'
                }`}
                style={{
                  clipPath: 'inset(0)',
                }}
              >
                {currentVideoId ? (
                  <YouTubePlayer videoId={currentVideoId} />
                ) : (
                  <input
                    type="image"
                    src={currentImage}
                    alt="Zoomable product image"
                    width={500}
                    height={500}
                    onClick={handleZoom}
                    className={`h-[500px] w-[500px] object-contain transition-transform duration-300 ease-in-out ${
                      zoomLevel === 4 ? 'cursor-zoom-out' : 'cursor-zoom-in'
                    }`}
                    style={{
                      transform: `scale(${zoomLevel})`,
                    }}
                  />
                )}
              </div>
              <IconButton
                aria-label="Next image"
                onClick={nextImage}
                isDisabled={images.length <= 1}
                className="bg-background absolute right-2 z-10 text-gray-500 hover:text-black"
              >
                <Icon icon={faChevronRight} />
              </IconButton>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="bg-background z-20 flex w-full shrink-0 flex-wrap items-center justify-end gap-4 border-t-1 border-t-gray-200 p-4 lg:gap-32 lg:p-6">
          <div className="flex items-center justify-center gap-4">
            {!currentVideoId && (
              <>
                <IconButton
                  aria-label="Zoom in"
                  variant="light"
                  size="md"
                  onClick={() => setZoomLevel(prev => Math.min(prev * 2, maxZoom))}
                  disabled={zoomLevel >= maxZoom}
                  className="text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon icon={faCirclePlus} size="xl" />
                </IconButton>

                <IconButton
                  aria-label="Zoom out"
                  variant="light"
                  size="md"
                  onClick={() => setZoomLevel(prev => Math.max(prev / 2, minZoom))}
                  disabled={zoomLevel <= minZoom}
                  className="text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon icon={faCircleMinus} size="xl" />
                </IconButton>

                <IconButton
                  aria-label="Reset zoom"
                  variant="light"
                  size="md"
                  onClick={() => setZoomLevel(1)}
                  disabled={zoomLevel <= minZoom}
                  className="text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icon icon={faRotateBack} size="xl" />
                </IconButton>
              </>
            )}
          </div>

          <div className="flex w-68 items-center justify-end gap-4">
            <FeatureFlag feature="favorites">
              <IconButton
                aria-label="Toggle favorite"
                variant="bordered"
                size="md"
                onClick={e => {
                  e.preventDefault();
                  if (isInFavorites) {
                    const currentEventId = crypto.randomUUID();
                    sendGTMEvent({ ecommerce: null });
                    sendGTMEvent({
                      event: 'remove_from_favorites',
                      event_id: currentEventId,
                      ecommerce: {
                        currency,
                        value: product.discountedPrice,
                        items: [
                          {
                            item_id: product.id,
                            item_name: product.name,
                            price: product.discountedPrice,
                            item_category: product.parentCategoryId || '',
                          },
                        ],
                      },
                    });
                    removeProductFromFavorites(product, {
                      name: 'RemoveFromWishlist',
                      id: currentEventId,
                      sourceUrl: window.location.href,
                    });
                  } else {
                    const currentEventId = crypto.randomUUID();
                    sendGTMEvent({ ecommerce: null });
                    sendGTMEvent({
                      event: 'add_to_favorites',
                      event_id: currentEventId,
                      ecommerce: {
                        currency,
                        value: product.discountedPrice,
                        items: [
                          {
                            item_id: product.id,
                            item_name: product.name,
                            price: product.discountedPrice,
                            item_category: product.parentCategoryId || '',
                          },
                        ],
                      },
                    });
                    addProductToFavorites(product, {
                      name: 'AddToWishlist',
                      id: currentEventId,
                      sourceUrl: window.location.href,
                    });
                  }
                }}
                isLoading={isFavoritePending(product.id)}
              >
                <Icon
                  icon={isInFavorites ? faHeart : faHeartRegular}
                  className={cn('!text-base', { 'text-red-800': isInFavorites })}
                />
              </IconButton>
            </FeatureFlag>
            <Button
              className={cn('flex items-center justify-center gap-2', {
                hidden: !shouldAllowCart,
                'mt-0': !shouldAllowCart,
              })}
              color="primary"
              size="md"
              onClick={e => {
                e.preventDefault();
                const currentEventId = crypto.randomUUID();
                sendGTMEvent({ ecommerce: null });
                sendGTMEvent({
                  event: 'add_to_cart',
                  event_id: currentEventId,
                  ecommerce: {
                    currency,
                    value: product.discountedPrice,
                    items: [
                      {
                        item_id: product.id,
                        item_name: product.name,
                        price: product.discountedPrice,
                        quantity: 1,
                        item_category: product.parentCategoryId || '',
                      },
                    ],
                  },
                });
                addProductToMyCart(
                  { ...product, quantity: 1 },
                  {
                    name: 'AddToCart',
                    id: currentEventId,
                    sourceUrl: window.location.href,
                  },
                );
              }}
              isLoading={isPendingUpdateMyCartProductId === product.id}
            >
              {isPendingUpdateMyCartProductId !== product.id && <Icon icon={faShoppingCart} />}{' '}
              {t?.common.addToCart}
            </Button>
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImagesModal;
